import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import ToolLinks from '@/components/page-specific/tools/tool-links'
import { useAuth, useUser } from '@clerk/nextjs'
import { ArrowRight, Check } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

export default function ResearchPaperGenerator() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  return (
    <>
      <NextSeo
        title="v0 report - The Ultimate AI Research Paper Generator"
        description="Elevate your research with v0 Report: the Ultimate AI Research Paper Generator. Generate high-quality research papers for any topic in minutes."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen flex-col ${inter.className}`}>
          <div className=" mt-36">
            <div className="mx-auto text-center">
              <h1 className="text-6xl text-zinc-700  grid gap-2">
                <span className="font-medium">v0 Report - The Ultimate AI</span>
                <span className="font-thin italic font-serif leading-none text-yellow-600">
                  Research Paper Generator
                </span>
              </h1>
            </div>
            <div className="ml-auto flex px-4 md:px-0 md:w-3/4 mx-auto mb-24">
              <div className="w-full mx-auto">
                <div className="grid gap-6">
                  <h2 className="text-4xl text-zinc-700 text-left mt-48">
                    <span className="font-medium">
                      Create Professional Papers with v0 Report’s{' '}
                    </span>
                    <span className="font-thin italic font-serif leading-none text-sky-600">
                      AI Research Paper Generator{' '}
                    </span>
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Organize and clarify your research papers effortlessly with v0 Report!
                    </span>
                    <span>
                      With our Research Paper Outline Generator, turn your scattered thoughts into a
                      well-structured academic research paper in just a few clicks.
                    </span>
                  </p>
                  <Link
                    className="w-fit h-10 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                    href={'/auth/signup'}
                  >
                    {' '}
                    Get started <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/report.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Let v0 Report Research and Write Your Paper in Five Prompts
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      With v0 Report, just give a title or a brief idea to generate a research
                      paper, and our AI research paper writer will swiftly locate sources on any
                      topic you want.
                    </span>
                    <span>
                      v0 Report helps you quickly create generated content such as the introduction,
                      discussion, and conclusion, saving you precious time and energy.
                    </span>
                  </p>

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    How the v0 Report Simplifies Research Paper Writing
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            AI-Powered Research Paper Generator
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Quickly find sources for any sentence or text using our built-in
                            research tool in the editor paragraph generator.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Diverse Research Paper Styles</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Choose from various research papers and academic paper formats,
                            including descriptive papers that simplify the creation process across
                            different styles and writing processes.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Professional and High Quality Research Paper Generator
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Produce the highest academic standards of level research papers with our
                            enhanced AI writing quality mode.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Organized Researched Text</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Develop a well-organized narrative, logical thought process, and
                            thorough introductions and conclusions for your research paper.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Automatic References</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Effortlessly include references in your research paper from Google,
                            Google Scholar, and other selected sources.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Writer’s Block Breaker</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Overcome writer’s block with our advanced AI writer researcher, making
                            starting and progressing your research paper easier.
                          </p>{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/on-laptop.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Why Choose the v0 Report AI Research Paper Generator?
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Creating a research paper can be difficult, especially when you're short on
                      time and resources. A research paper generator like the v0 Report can simplify
                      your workflow by swiftly locating and organizing relevant sources related to
                      your topic.
                    </span>
                    <span>
                      With our AI text generator tool, you can create high-quality papers in just
                      minutes, freeing up more time for in-depth analysis and writing.
                    </span>
                  </p>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Topic Input</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Enter the topic or question you want your research paper to address.
                            This sets the direction for the content generation process.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Research and Data Collection</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            The AI adds databases, journals, and other digital resources to gather
                            data, studies, and scholarly articles relevant to your topic. It uses
                            advanced research paper algorithms to filter and prioritize the most
                            important information.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Structure and Template Selection</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Choose from a variety of research essay generator templates that best
                            fit the style of research paper you need, such as analytical,
                            expository, or argumentative. The AI then structures the gathered
                            information according to the selected template.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Drafting the Content</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Using natural language processing (NLP) technologies, the AI composes a
                            coherent and logically structured draft of your paper, integrating
                            citations and correctly formatting them according to the chosen academic
                            style (APA, MLA, etc.).
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Review and Refinement</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            The draft undergoes checks and revisions to ensure accuracy, clarity,
                            and coherence. You can review it, make any necessary adjustments, and
                            refine the content using the AI’s suggestions before finalizing the
                            document.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Immediately Identify Sources for Any Research Phrase
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      v0 Report, our powerful AI research paper generator assistant, integrated
                      within the research paper editor interface, simplifies the process of sourcing
                      and fact-checking any text from the web.
                    </span>
                    <span>
                      It efficiently locates the most relevant information and its source, allowing
                      you to add references to your document with just a click.
                    </span>
                    <span>
                      Additionally, our research content generator tool offers various research
                      modes, such as “find support statistics,” “find supporting arguments,” and
                      “find useful information,” to streamline your search for necessary data.
                    </span>
                    <span>
                      With our AI research assistant, writing and researching for your paper becomes
                      effortless.
                    </span>
                  </p>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Quickly Acknowledge References
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Our research paper generator simplifies the process of citing sources in MLA
                      and APA styles. It starts by identifying key details in each source—like the
                      author, title, publication date, and URL—and then formats them correctly
                      according to your chosen citation style.
                    </span>
                    <span>
                      With tools that help generate citations and bibliographies correctly, you can
                      easily document your sources and prevent plagiarism.
                    </span>
                    <span>
                      Whether a student or a professional writer, our generator saves you time on
                      citations, letting you concentrate on writing your paper.
                    </span>
                  </p>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    AI Research Paper Generator & Writer - Overcome Writer's Block Effortlessly!
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Are you stuck with writer's block, especially when tackling research papers?
                      Do you want to create an outstanding paper but lack the spark of inspiration?
                    </span>
                    <span>
                      Say farewell to writer's block with the v0 Report’s AI Research Paper
                      Generator & Writer!
                    </span>
                    <span>
                      v0 Report analyzes millions of papers and utilizes advanced algorithms to
                      create unique and original research content.
                    </span>
                    <span>
                      Simply enter your academic writing topic, and the v0 Report’s Research Paper
                      Generator will generate high-quality papers quickly and efficiently.
                    </span>
                  </p>
                </div>
              </div>
              <div className="hidden sm:block">
                <ToolLinks />
              </div>
            </div>
            <div className="w-fit grid gap-8 bg-sky-50 rounded-xl py-8 md:py-20 px-5 md:px-24 mx-2 md:mx-auto">
              <p className="text-2xl font-semibold text-gray-900 ">Get Started Today!</p>
              <p className="text-xl tracking-tight font-medium text-zinc-600 grid gap-2">
                <span>
                  Embrace the efficiency of the automated AI report writing tool with the v0 AI
                  Report Generator.
                </span>
                <span>
                  Sign up now or request a demo to see how our report writer tool can revolutionize
                  how you work with your report data.{' '}
                </span>
                <span>Transform your report analysis with v0 Report's accuracy and speed!</span>
              </p>
            </div>
            <div className="w-11/12 mx-auto my-24 md:w-fit text-center grid gap-8">
              <Link
                rel="canonical"
                href={isSubscribed ? '/home' : '/'}
                className="flex justify-center w-fit mx-auto"
              >
                <Image
                  className="object-scale-down h-8 w-8"
                  src="/images/logo.png"
                  alt="Logo"
                  width={200}
                  height={300}
                />
                <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                  v0 report
                </p>
              </Link>
              <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900 ">
                Experience the future of document generation today <br />
                with v0 Report!
              </h2>
              <Link
                className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                href={'/auth/signup'}
              >
                Get started
              </Link>
            </div>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
